<template>
  <div
    class="square-location"
    :class="{ 'disabled-location': inactive, 'active-location': active }"
  >
    <div
      v-if="inactive"
      class="disabled-watermark"
    >
      DISABLED
    </div>
    <div class="display-name">
      {{ displayName }}
    </div>
    <div
      v-if="description"
      class="description"
    >
      {{ description }}
    </div>
    <div class="location-address">
      <span class="line1">{{ line1 }}</span><br>
      <template v-if="line2">
        <span class="line2">{{ line2 }}</span><br>
      </template>
      <span class="city">{{ city }}</span>&nbsp;
      <span class="state">{{ state }}</span>&nbsp;
      <span class="postal-code">{{ postal_code }}</span>
      <br>
      <span class="country">{{ country }}</span>
    </div>

    <!-- Action buttons container -->
    <div class="action-buttons">
      <b-button
        variant="primary"
        size="sm"
        :disabled="!active"
        @click="requestEdit"
      >
        <font-awesome-icon
          icon="pen"
          class="me-1"
        />
        edit location
      </b-button>

      <b-button
        v-if="active"
        variant="danger"
        size="sm"
        data-cy="deactivate-location-button"
        @click="requestDeactivate"
      >
        deactivate
      </b-button>

      <b-button
        v-if="inactive"
        variant="success"
        size="sm"
        data-cy="activate-location-button"
        @click="requestActivate"
      >
        activate
      </b-button>
    </div>
  </div>
</template>

<script>
import addressUtils from '../../mixins/address-utils'

export default {
  name: 'square-location-details',
  mixins: [addressUtils],
  props: {
    location: {
      type: Object,
      required: true
    },
    remove: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      processing: false
    }
  },
  computed: {
    displayName: function() {
      return this.location.name
    },
    active: function() {
      return this.location.status === 'ACTIVE'
    },
    inactive: function() {
      return this.location.status === 'INACTIVE'
    },
    description: function() {
      return this.location.description || false
    },
    line1: function() {
      return this.location.address.address_line_1
    },
    line2: function() {
      return typeof this.location.address.address_line_2 === 'string' && this.location.address.address_line_2.length ? this.location.address.address_line_2 : null
    },
    city: function() {
      return this.location.address.locality
    },
    state: function() {
      return this.location.address.administrative_district_level_1
    },
    postal_code: function() {
      return this.location.address.postal_code
    },
    country: function() {
      return this.location.address.country
    },
    isLocationActive: function() {
      return this.active && !this.disabled
    }
  },
  methods: {
    requestEdit: function() {
      this.$emit('edit-requested', this.location)
    },
    requestDeactivate() {
      this.$emit('deactivated', this.location)
    },
    requestActivate() {
      this.$emit('activated', this.location)
    }
  }
}
</script>

<style lang="scss" scoped>
.square-location {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1; /* Fill the parent container */
  height: 100%; /* Take full height of parent */
  background-color: #ffffff;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;

  &.disabled-location {
    background-color: #f5f5f5;

    .display-name,
    .description,
    .details {
      opacity: 0.7;
    }
  }

  .disabled-watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-25deg);
    font-size: 2.5rem;
    font-weight: 900;
    color: rgba(128, 128, 128, 0.3);
    z-index: 1;
    pointer-events: none;
    user-select: none;
  }

  .display-name {
    font-weight: bold;
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    position: relative;
    z-index: 2;
  }

  .description {
    margin-bottom: 0.5rem;
    position: relative;
    z-index: 2;
  }

  .details {
    flex: 1; /* Take up remaining space, pushing button to bottom */
    word-wrap: break-word; /* Handle long addresses */
    margin-bottom: 40px; /* Reserve space for button */
    position: relative;
    z-index: 2;
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 1;

    .action-button {
      flex-shrink: 1;
      max-width: fit-content;
    }
  }
}
</style>
